import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Zap, Instagram, Video, ArrowDown } from 'lucide-react';
import { AlertCircle } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.cardBackground};
  border-radius: 16px;
  width: 90%;
  max-width: 550px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(165, 145, 239, 0.3);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textColor};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textColor};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.hoverBackground};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DeviceInfo = styled.div`
  background: ${props => props.theme.hoverBackground};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

const DeviceInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }
`;

const DeviceInfoLabel = styled.span`
  color: ${props => props.theme.mutedTextColor};
  font-size: 14px;
  font-weight: 500;
`;

const DeviceInfoValue = styled.span`
  color: ${props => props.theme.textColor};
  font-size: 14px;
  font-weight: 600;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(225, 48, 108, 0.1);
  border: 1px solid rgba(225, 48, 108, 0.3);
  border-radius: 12px;
  margin-bottom: 20px;
`;

const InfoText = styled.p`
  margin: 0;
  color: ${props => props.theme.textColor};
  font-size: 14px;
  line-height: 1.5;
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${props => props.checked ? 'rgba(225, 48, 108, 0.1)' : props.theme.hoverBackground};
  border: 2px solid ${props => props.checked ? 'rgba(225, 48, 108, 0.5)' : props.theme.borderColor};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(225, 48, 108, 0.15);
    border-color: rgba(225, 48, 108, 0.6);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #E1306C;
  margin-top: 2px;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textColor};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${props => props.theme.mutedTextColor};
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 24px;
  border-top: 1px solid ${props => props.theme.borderColor};
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.borderColor};
  background: transparent;
  color: ${props => props.theme.textColor};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.textColor};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LaunchButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #E1306C, #C13584);
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(225, 48, 108, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InstagramModal = ({ device, onClose, onLaunch, theme }) => {
  const [withRestarter, setWithRestarter] = useState(false);
  const [closeAppsAfter, setCloseAppsAfter] = useState(true);
  const [debugRecording, setDebugRecording] = useState(false);
  const [bigScrollCount, setBigScrollCount] = useState(3);
  const [isLaunching, setIsLaunching] = useState(false);

  const handleLaunch = async () => {
    setIsLaunching(true);
    try {
      await onLaunch(device.device_id, device.account_id, withRestarter, closeAppsAfter, debugRecording, bigScrollCount);
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>
            <Instagram size={24} color="#E1306C" />
            Script Interact Instagram
          </ModalTitle>
          <CloseButton theme={theme} onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <DeviceInfo theme={theme}>
            <DeviceInfoRow>
              <DeviceInfoLabel theme={theme}>Device</DeviceInfoLabel>
              <DeviceInfoValue theme={theme}>{device.device_name || device.device_id}</DeviceInfoValue>
            </DeviceInfoRow>
            <DeviceInfoRow>
              <DeviceInfoLabel theme={theme}>ID</DeviceInfoLabel>
              <DeviceInfoValue theme={theme}>{device.device_id}</DeviceInfoValue>
            </DeviceInfoRow>
            <DeviceInfoRow>
              <DeviceInfoLabel theme={theme}>Compte Instagram</DeviceInfoLabel>
              <DeviceInfoValue theme={theme}>{device.account_username || 'N/A'}</DeviceInfoValue>
            </DeviceInfoRow>
            {device.workflow && (
              <DeviceInfoRow>
                <DeviceInfoLabel theme={theme}>Workflow</DeviceInfoLabel>
                <DeviceInfoValue theme={theme}>{device.workflow}</DeviceInfoValue>
              </DeviceInfoRow>
            )}
          </DeviceInfo>

          <InfoBox theme={theme}>
            <AlertCircle size={16} color={theme.primary} style={{ marginTop: '2px', flexShrink: 0 }} />
            <InfoText theme={theme}>
              Le script va lancer les interactions Instagram automatiques sur ce device avec le compte associé.
            </InfoText>
          </InfoBox>

          <OptionSection>
            <OptionLabel 
              theme={theme} 
              checked={withRestarter}
            >
              <Checkbox
                type="checkbox"
                checked={withRestarter}
                onChange={(e) => setWithRestarter(e.target.checked)}
                theme={theme}
              />
              <OptionContent>
                <OptionTitle theme={theme}>
                  <Zap size={16} color={theme.primary} />
                  Lancer le restarteur avant
                </OptionTitle>
                <OptionDescription theme={theme}>
                  Redémarre la connexion du device via le restarteur associé au workflow avant de lancer le script Instagram. Recommandé si le device a des problèmes de connexion.
                </OptionDescription>
              </OptionContent>
            </OptionLabel>
            
            <OptionLabel 
              theme={theme} 
              checked={closeAppsAfter}
            >
              <Checkbox
                type="checkbox"
                checked={closeAppsAfter}
                onChange={(e) => setCloseAppsAfter(e.target.checked)}
                theme={theme}
              />
              <OptionContent>
                <OptionTitle theme={theme}>
                  <X size={16} color={theme.primary} />
                  Fermer les applications après
                </OptionTitle>
                <OptionDescription theme={theme}>
                  Ferme automatiquement toutes les applications récentes après l'exécution du script Instagram. Recommandé pour nettoyer le device.
                </OptionDescription>
              </OptionContent>
            </OptionLabel>
            
            <OptionLabel 
              theme={theme} 
              checked={debugRecording}
            >
              <Checkbox
                type="checkbox"
                checked={debugRecording}
                onChange={(e) => setDebugRecording(e.target.checked)}
                theme={theme}
              />
              <OptionContent>
                <OptionTitle theme={theme}>
                  <Video size={16} color="var(--error)" />
                  Mode Debug Recording 🎬
                </OptionTitle>
                <OptionDescription theme={theme}>
                  Enregistre des screenshots à chaque log important et génère une vidéo avec les logs synchronisés pour le debug. La vidéo sera sauvegardée dans debug-recordings/{'{'}username{'}'}/
                </OptionDescription>
              </OptionContent>
            </OptionLabel>
            
            <OptionLabel theme={theme}>
              <OptionContent style={{width: '100%'}}>
                <OptionTitle theme={theme}>
                  <ArrowDown size={16} color="var(--success)" />
                  Nombre de Big Scrolls
                </OptionTitle>
                <OptionDescription theme={theme}>
                  Nombre de scrolls importants à effectuer quand trop d'utilisateurs sont déjà suivis ou déjà interagis (par défaut: 3)
                </OptionDescription>
                <div style={{marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={bigScrollCount}
                    onChange={(e) => setBigScrollCount(parseInt(e.target.value) || 3)}
                    style={{
                      width: '80px',
                      padding: '8px',
                      borderRadius: '6px',
                      border: `1px solid ${theme.border}`,
                      backgroundColor: theme.cardBg,
                      color: theme.text,
                      fontSize: '14px'
                    }}
                  />
                  <span style={{color: theme.textSecondary, fontSize: '13px'}}>scrolls</span>
                </div>
              </OptionContent>
            </OptionLabel>
          </OptionSection>
        </ModalBody>

        <ModalFooter theme={theme}>
          <CancelButton theme={theme} onClick={onClose} disabled={isLaunching}>
            Annuler
          </CancelButton>
          <LaunchButton onClick={handleLaunch} disabled={isLaunching}>
            <Instagram size={18} />
            {isLaunching ? 'Lancement...' : 'Lancer le Script'}
          </LaunchButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default InstagramModal;
